import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { CheckCircle, XCircle, Clock, Calendar, FileText, User, Filter, Search } from 'lucide-react';
import { formatDate } from '../../utils/dateTimeUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function LeaveApproval() {
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [filter, setFilter] = useState('Pending');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [remarks, setRemarks] = useState('');

  useEffect(() => {
    fetchRequests();
  }, [filter]);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const url = filter === 'All' 
        ? `${API_URL}/api/leave-approval/all`
        : `${API_URL}/api/leave-approval/all?status=${filter}`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setRequests(data.requests || []);
        setStats(data.stats || { pending: 0, approved: 0, rejected: 0 });
      }
    } catch (error) {
      console.error('Error fetching requests:', error);
      toast.error('Failed to fetch leave requests');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedRequest) return;
    
    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/leave-approval/${selectedRequest.id}/approve`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ remarks })
      });

      if (response.ok) {
        toast.success('Leave request approved!');
        setSelectedRequest(null);
        setRemarks('');
        fetchRequests();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to approve');
      }
    } catch (error) {
      toast.error('Failed to approve leave');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedRequest) return;
    if (!remarks.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    
    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/leave-approval/${selectedRequest.id}/reject`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ remarks })
      });

      if (response.ok) {
        toast.success('Leave request rejected');
        setSelectedRequest(null);
        setRemarks('');
        fetchRequests();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to reject');
      }
    } catch (error) {
      toast.error('Failed to reject leave');
    } finally {
      setProcessing(false);
    }
  };

  const calculateDays = (from, to) => {
    if (!from || !to) return 0;
    return Math.ceil((new Date(to) - new Date(from)) / (1000 * 60 * 60 * 24)) + 1;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Approved': return 'bg-green-100 text-green-700';
      case 'Rejected': return 'bg-red-100 text-red-700';
      default: return 'bg-yellow-100 text-yellow-700';
    }
  };

  const getLeaveTypeColor = (type) => {
    switch (type) {
      case 'Casual Leave': return 'bg-blue-100 text-blue-700';
      case 'Sick Leave': return 'bg-red-100 text-red-700';
      case 'Earned Leave': return 'bg-green-100 text-green-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="leave-approval-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Leave Approval</h1>
          <p className="text-gray-500">Manage staff leave requests</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-yellow-50 border-yellow-200 cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => setFilter('Pending')}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Clock className="h-8 w-8 text-yellow-600" />
              <div>
                <p className="text-2xl font-bold text-yellow-700">{stats.pending}</p>
                <p className="text-sm text-yellow-600">Pending</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-green-50 border-green-200 cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => setFilter('Approved')}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="h-8 w-8 text-green-600" />
              <div>
                <p className="text-2xl font-bold text-green-700">{stats.approved}</p>
                <p className="text-sm text-green-600">Approved</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-red-50 border-red-200 cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => setFilter('Rejected')}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <XCircle className="h-8 w-8 text-red-600" />
              <div>
                <p className="text-2xl font-bold text-red-700">{stats.rejected}</p>
                <p className="text-sm text-red-600">Rejected</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-50 border-gray-200 cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => setFilter('All')}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <FileText className="h-8 w-8 text-gray-600" />
              <div>
                <p className="text-2xl font-bold text-gray-700">{stats.pending + stats.approved + stats.rejected}</p>
                <p className="text-sm text-gray-600">Total</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter Badge */}
      <div className="flex items-center gap-2">
        <Filter className="h-4 w-4 text-gray-500" />
        <span className="text-sm text-gray-500">Showing:</span>
        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
          filter === 'Pending' ? 'bg-yellow-100 text-yellow-700' :
          filter === 'Approved' ? 'bg-green-100 text-green-700' :
          filter === 'Rejected' ? 'bg-red-100 text-red-700' :
          'bg-gray-100 text-gray-700'
        }`}>
          {filter}
        </span>
      </div>

      {/* Requests List */}
      <Card>
        <CardHeader>
          <CardTitle>Leave Requests</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          ) : requests.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Calendar className="h-12 w-12 mx-auto mb-2 text-gray-300" />
              <p>No {filter.toLowerCase()} leave requests</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Staff</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Leave Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Duration</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Days</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {requests.map((request) => (
                    <tr key={request.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium text-gray-900">{request.staff_name}</p>
                          <p className="text-sm text-gray-500">{request.employee_id}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${getLeaveTypeColor(request.leave_type)}`}>
                          {request.leave_type}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {formatDate(request.from_date)}
                        {' - '}
                        {formatDate(request.to_date)}
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-medium text-indigo-600">
                          {calculateDays(request.from_date, request.to_date)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(request.status)}`}>
                          {request.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {request.status === 'Pending' ? (
                          <Button
                            size="sm"
                            onClick={() => setSelectedRequest(request)}
                            data-testid={`review-btn-${request.id}`}
                          >
                            Review
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setSelectedRequest(request)}
                          >
                            View
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Review Modal */}
      {selectedRequest && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-bold">
                {selectedRequest.status === 'Pending' ? 'Review Leave Request' : 'Leave Request Details'}
              </h3>
              <button onClick={() => { setSelectedRequest(null); setRemarks(''); }} className="text-gray-400 hover:text-gray-600">
                ✕
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center gap-3 mb-3">
                  <User className="h-10 w-10 text-gray-400 bg-white rounded-full p-2" />
                  <div>
                    <p className="font-semibold text-gray-900">{selectedRequest.staff_name}</p>
                    <p className="text-sm text-gray-500">{selectedRequest.employee_id}</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div>
                    <p className="text-xs text-gray-500">Leave Type</p>
                    <p className="font-medium">{selectedRequest.leave_type}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Days</p>
                    <p className="font-medium text-indigo-600">
                      {calculateDays(selectedRequest.from_date, selectedRequest.to_date)} days
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">From</p>
                    <p className="font-medium">{formatDate(selectedRequest.from_date)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">To</p>
                    <p className="font-medium">{formatDate(selectedRequest.to_date)}</p>
                  </div>
                </div>

                {selectedRequest.reason && (
                  <div className="mt-4">
                    <p className="text-xs text-gray-500">Reason</p>
                    <p className="text-sm bg-white p-2 rounded mt-1">{selectedRequest.reason}</p>
                  </div>
                )}

                {selectedRequest.admin_remarks && (
                  <div className="mt-4">
                    <p className="text-xs text-gray-500">Admin Remarks</p>
                    <p className="text-sm bg-white p-2 rounded mt-1">{selectedRequest.admin_remarks}</p>
                  </div>
                )}
              </div>

              {selectedRequest.status === 'Pending' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Remarks (Required for rejection)
                  </label>
                  <textarea
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg"
                    rows={2}
                    placeholder="Add your remarks..."
                  />
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => { setSelectedRequest(null); setRemarks(''); }}
                  className="flex-1"
                >
                  Close
                </Button>
                
                {selectedRequest.status === 'Pending' && (
                  <>
                    <Button
                      onClick={handleReject}
                      disabled={processing}
                      className="flex-1 bg-red-600 hover:bg-red-700"
                      data-testid="reject-btn"
                    >
                      <XCircle className="h-4 w-4 mr-2" />
                      Reject
                    </Button>
                    <Button
                      onClick={handleApprove}
                      disabled={processing}
                      className="flex-1 bg-green-600 hover:bg-green-700"
                      data-testid="approve-btn"
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Approve
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
